<template>
    <section class="home-events-top">
        <ResourcePageHeroShape v-if="!home?.data.slices?.length" class="home-events-top__shape resource-page--default" />
        <div class="content">
            <Breadcrumbs class="breadcrumbs" />
            <PrismicRichText :field="home?.data.pageTitle" :class="['page-title', {'page-title--without-slices': !home?.data.slices?.length}]" />
        </div>
    </section>
    <section class="home-events-hero">
        <SliceZone v-if="home" :slices="home.data.slices" :components="components" />
    </section>
    <section class="events-grid-wrapper">
        <MktTabs v-model="currentTab" :tabs="tabs" />
        <h2 class="change-list-title">{{ currentTitle }}</h2>
        <CategorySelector class="categories-filters" :current-tab="currentTab" />

        <div v-if="pending || !events.length" class="events-grid__loader">
            <VJoySpinner v-if="pending" class="events-grid__spinner" />
            <template v-else-if="!events.length">{{ t('generic.no.results.found') }}</template>
        </div>
        <div v-else class="events-grid">
            <template v-for="(event, index) of events" :key="event.uid ?? ''">
                <EventCard :event="event" />
                <CTACard v-if="hasHomeDoc && index === 4" v-bind="ctaCardConfig" />
            </template>
            <CTACard v-if="hasHomeDoc && events.length < 5" v-bind="ctaCardConfig" />
        </div>

        <Pagination
            v-if="pagination && pagination.total > 1"
            base-path="/resources/events"
            :total-pages="pagination.total"
            :current-page="pagination.page"
        />
    </section>

    <SliceZone v-if="home" :slices="home?.data.slices2" :components="components" />
</template>

<script lang="ts" setup>
    import {computed, inject, ref, watch} from 'vue';
    import {filter} from '@prismicio/client';
    import {PrismicRichText, SliceZone} from '@prismicio/vue';
    import type {EventDocument, HomeEventsDocument} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {MktTabs} from '#components';
    import {
        baseSearchPredicatesByCustomType,
        createError,
        useAsyncData,
        useHead,
        usePrismicTypeGuards,
        useRequestURL,
        useTranslation,
        useWebsitePageView,
        useLocalePrefix,
    } from '#imports';
    import {VJoySpinner} from '@maltjoy/core-vue';
    import type {AUDIENCES, CATEGORIES} from '~/app/keys';
    import Breadcrumbs from '~/components/Breadcrumbs.vue';
    import CategorySelector from '~/components/CategorySelector.vue';
    import CTACard from '~/components/CTACard.vue';
    import EventCard from '~/components/EventCard.vue';
    import Pagination from '~/components/Pagination.vue';
    import ResourcePageHeroShape from '~/components/ResourcePageHeroShape.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';
    import {components} from '~/slices/components';

    enum Tabs {
        Online = 'online',
        Offline = 'offline',
        Replay = 'replay',
    }

    // Init
    const {hasProperty} = usePrismicTypeGuards();
    const {t} = useTranslation();
    const {withLocalePrefix} = useLocalePrefix();
    const route = useRoute('resources-events-page');
    const {params, query} = route;
    const page = params.page;
    const pageNum = typeof page === 'string' && Number(page) > 0 ? Number(page) : 1;

    // Refs
    const home = ref<HomeEventsDocument>();
    const events = ref<EventDocument[]>([]);
    const pagination = ref<{total: number; page: number}>();

    // Category and Type
    const {data: resourceCategories} = await useAsyncData('categories', () => fetchCategories(), {deep: true});
    const queryCategory = typeof query?.category === 'string' ? (query?.category as (typeof CATEGORIES)[number]) : undefined;
    const queryAudience = typeof query?.audience === 'string' ? (query?.audience as (typeof AUDIENCES)[number]) : undefined;
    const queryType = (query.type as string) || Tabs.Online;

    const categoryId = computed(() => {
        const found = resourceCategories.value?.results.find((rcat) => rcat.uid === route.query?.category);
        return found ? found.id : undefined;
    });
    const refType = computed(() => (route.query.type as string) || Tabs.Online);

    const currentTab = ref(queryType);

    // Tabs
    const buildTabs = (doc: HomeEventsDocument) => {
        return (doc?.data.tabs || []).map((tab) => {
            const tabKey = tab.key as string;
            return {
                name: tabKey,
                label: tab.label as string,
                href: withLocalePrefix(`/resources/events?type=${tabKey}`),
                title: tab?.title?.map((node) => (hasProperty(node, 'text') ? node.text : '')).join('') || '',
            };
        });
    };

    // Date
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = yesterdayDate.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];

    // Fetch Home and Events
    const {data, pending} = await useAsyncData(
        () => `events:${route.fullPath}`,
        () => fetchAll(),
        {
            deep: true,
        },
    );
    if (!data.value) {
        throw createError({
            message: `No events found for category ${queryCategory} and audience ${queryAudience}`,
            statusCode: 404,
        });
    }

    // Watcher
    watch(
        data,
        (newData) => {
            if (newData) {
                home.value = newData[0].results[0] as HomeEventsDocument;
                events.value = newData[1].results as EventDocument[];
                pagination.value = {total: newData[1].total_pages, page: newData[1].page};
            }
        },
        {immediate: true},
    );

    // HomeDocument
    const hasHomeDoc = home.value && home.value.data.title && home.value.data.ctaLink && home.value.data.ctaText;
    const tabs = ref<{label: string; name: string; title: string; href: string}[]>(buildTabs(home.value!));
    if (!home.value) {
        const fallbackdataHome = await fetchHome(true); // fallback to default page : all audience and no category
        const fallbackHomeDocument = fallbackdataHome?.results[0];
        if (!fallbackHomeDocument) {
            throw createError({
                message: `No home events found for category ${queryCategory} and audience ${queryAudience}`,
                statusCode: 404,
            });
        }
        tabs.value = buildTabs(fallbackHomeDocument as HomeEventsDocument);
        home.value = fallbackHomeDocument as HomeEventsDocument;
    }
    const currentTitle = computed(() => tabs.value.find((tab) => tab.name === currentTab.value)?.title);
    const ctaCardConfig = ref({
        class: 'cta',
        title: home.value?.data?.title,
        text: home.value?.data?.text,
        ctaLink: home.value?.data?.ctaLink,
        ctaText: home.value?.data?.ctaText,
    });

    // Functions
    function fetchAll() {
        return Promise.all([fetchHome(), fetchEvents()]);
    }
    function fetchHome(fallback = false) {
        return usePrismicSearch<HomeEventsDocument>(
            {
                type: 'homeEvents',
                audience: !fallback && queryAudience ? queryAudience : 'all',
                category: !fallback ? (route.query.category as string) : 'all',
                pageSize: 1,
            },
            true,
        );
    }
    function fetchEvents() {
        const predicates = [...(baseSearchPredicatesByCustomType.event.predicates ?? [])];

        if (refType.value === 'replay') {
            predicates.push(filter.dateBefore('my.event.date', today));
            predicates.push(filter.has('my.event.replayLink'));
        } else {
            predicates.push(filter.dateAfter('my.event.date', yesterday));
        }
        if (refType.value === 'offline') {
            predicates.push(filter.at('my.event.eventType', 'offline'));
        } else {
            predicates.push(filter.not('my.event.eventType', 'offline'));
        }

        const catId = categoryId.value;

        return usePrismicSearch(
            {
                type: 'event',
                category: route.query.category as string,
                predicates,
                audience: queryAudience !== 'all' ? queryAudience : undefined,
                orderings: [
                    {
                        field: 'my.event.date',
                        direction: refType.value === Tabs.Replay ? 'desc' : 'asc',
                    },
                ],
                pageSize: 11,
                page: pageNum,
                resourceCategoryId: catId,
            },
            true,
        );
    }
    function fetchCategories() {
        return usePrismicSearch({type: 'resourceCategory'});
    }

    // Canonical
    const {origin, pathname} = useRequestURL();
    const canonicalUrl = `${origin}${pathname}`;
    useHead({link: [{rel: 'canonical', href: canonicalUrl}]});

    // Tracking
    useWebsitePageView('resources-center', 'events');
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .home-events-top {
        padding: var(--joy-core-spacing-9) 0 var(--joy-core-spacing-4);
        background-color: var(--joy-color-quaternary-10);
        position: relative;

        > .home-events-top__shape {
            height: 220px;
            width: auto;
        }

        .breadcrumbs {
            margin-bottom: var(--joy-core-spacing-7);
        }

        .content {
            position: relative;
            z-index: 1;

            @include content-width;

            .page-title {
                h2 {
                    font-family: var(--joy-font-family-title);
                    font-size: var(--joy-font-size-secondary-200);
                    font-weight: var(--joy-font-weight-normal);
                    color: var(--joy-color-neutral-60);
                }

                &--without-slices {
                    padding-bottom: var(--joy-core-spacing-8);
                }
            }
        }
    }

    .home-events-hero .events-list {
        padding-top: var(--joy-core-spacing-9);

        .top-row {
            display: none;
        }
    }

    .home-events-hero .pinned-content-hero {
        padding: var(--joy-core-spacing-9) 0 80px;
    }

    .events-grid-wrapper {
        padding: 80px 0;

        @include content-width;

        .list-title {
            margin-bottom: var(--joy-core-spacing-5);
        }

        .change-list-title {
            text-align: center;
            margin-bottom: var(--joy-core-spacing-8);
        }

        .categories-filters {
            margin-bottom: var(--joy-core-spacing-9);
        }

        .button-row {
            display: flex;
            justify-content: center;
            margin-bottom: var(--joy-core-spacing-11);

            @media (max-width: $mobile-breakpoint) {
                margin-bottom: var(joy-core-spacing-6);
            }
        }

        .events-grid {
            display: grid;
            row-gap: var(--joy-core-spacing-5);
            grid-template-columns: minmax(0, 1fr);
            margin-bottom: var(joy-core-spacing-12);

            @include mq.screen_sm(min) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: var(--joy-core-spacing-4) var(--joy-core-spacing-3);
                margin-left: -15px;
                margin-right: -15px;
            }

            @include mq.screen_lg(min) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
                gap: var(--joy-core-spacing-6) var(--joy-core-spacing-4);
                margin-bottom: 80px;
            }

            .cta {
                justify-self: stretch;
            }
        }
    }

    .events-grid {
        &__loader {
            min-height: 250px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &__spinner {
            --spinner-width: 4px !important;
            --spinner-size: 48px !important;
            --spinner-default-color: var(--joy-color-primary-50) !important;
        }
    }
</style>
